import { Link } from "react-router-dom";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <div className="max-w-[800px] mx-auto px-6 lg:px-8 py-16 sm:py-20">
        <h1 className="text-[32px] sm:text-[40px] font-normal text-[#202124] leading-tight mb-2">Privacy Policy</h1>
        <p className="text-[13px] text-[#80868b] mb-10">Last updated: March 29, 2026</p>

        <div className="prose-eamico space-y-8 text-[15px] text-[#3c4043] leading-relaxed">

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">1. Introduction</h2>
            <p>
              Eamico Inc. ("Eamico," "we," "us," or "our"), a Federal Canadian corporation (Corporation Number 1781131-4, Business Number 792360372), and a subsidiary of Aikaro Technologies Corp., is committed to protecting the privacy and personal information of our users, visitors, and customers.
            </p>
            <p className="mt-3">
              This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website, use our products (Eamico Studio and Eamico Cloud), or interact with our services. This policy applies to users in Canada, the United States, and internationally.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">2. Regulatory Compliance</h2>
            <p>This Privacy Policy is designed to comply with:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li><strong>Canada:</strong> Personal Information Protection and Electronic Documents Act (PIPEDA), Canada's Anti-Spam Legislation (CASL), and applicable provincial privacy legislation</li>
              <li><strong>United States:</strong> California Consumer Privacy Act (CCPA) / California Privacy Rights Act (CPRA), Virginia Consumer Data Protection Act (VCDPA), Colorado Privacy Act (CPA), Connecticut Data Privacy Act (CTDPA), and other applicable state privacy laws</li>
              <li><strong>Children's Privacy:</strong> Children's Online Privacy Protection Act (COPPA). Our services are not directed to children under 13, and we do not knowingly collect personal information from children under 13.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">3. Information We Collect</h2>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">3.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Waitlist & Account Registration:</strong> Email address, name, and any other information you voluntarily provide when joining our waitlist or creating an account.</li>
              <li><strong>Communications:</strong> Information you provide when contacting us, including email address and message content.</li>
              <li><strong>Payment Information:</strong> If you purchase paid services, payment details are processed by our third-party payment processor. We do not store your full credit card number.</li>
            </ul>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">3.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent, click patterns, and interaction data.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, screen resolution, and language preferences.</li>
              <li><strong>Network Information:</strong> IP address, approximate geographic location, referring URL, and internet service provider.</li>
              <li><strong>Cookies & Tracking Technologies:</strong> As described in our <Link to="/cookies" className="text-[#ff9a4d] hover:underline">Cookie Policy</Link>.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">4. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li>To provide, operate, and maintain our services</li>
              <li>To process your waitlist registration and send you product updates</li>
              <li>To communicate with you, respond to inquiries, and provide customer support</li>
              <li>To improve, personalize, and develop our products and services</li>
              <li>To detect, investigate, and prevent fraudulent or unauthorized activity</li>
              <li>To comply with legal obligations and enforce our terms</li>
              <li>To send you marketing communications (with your consent, where required)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">5. Legal Basis for Processing (Canada)</h2>
            <p>Under PIPEDA, we process your personal information based on:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li><strong>Consent:</strong> Where you have provided express or implied consent</li>
              <li><strong>Contractual Necessity:</strong> Where processing is necessary to fulfill our agreement with you</li>
              <li><strong>Legitimate Interests:</strong> Where processing is necessary for our legitimate business interests, balanced against your privacy rights</li>
              <li><strong>Legal Compliance:</strong> Where processing is required to comply with applicable laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">6. Disclosure of Information</h2>
            <p>We may share your personal information with:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5">
              <li><strong>Aikaro Technologies Corp.:</strong> Our parent company, for business operations and administration</li>
              <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our services (hosting, email delivery, analytics, payment processing)</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, legal process, or governmental request</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, reorganization, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you have given us explicit permission to share your information</li>
            </ul>
            <p className="mt-3">We do not sell your personal information to third parties.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">7. Your Rights</h2>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">7.1 Canadian Residents (PIPEDA)</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Right to access your personal information</li>
              <li>Right to request correction of inaccurate information</li>
              <li>Right to withdraw consent (subject to legal or contractual restrictions)</li>
              <li>Right to file a complaint with the Office of the Privacy Commissioner of Canada</li>
            </ul>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">7.2 California Residents (CCPA/CPRA)</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Right to know what personal information is collected, used, shared, or sold</li>
              <li>Right to delete personal information</li>
              <li>Right to opt out of the sale or sharing of personal information</li>
              <li>Right to correct inaccurate personal information</li>
              <li>Right to limit use and disclosure of sensitive personal information</li>
              <li>Right to non-discrimination for exercising your privacy rights</li>
            </ul>

            <h3 className="text-[16px] font-medium text-[#202124] mt-4 mb-2">7.3 Other U.S. State Residents</h3>
            <p>Residents of Virginia, Colorado, Connecticut, and other states with comprehensive privacy laws have similar rights including access, deletion, correction, opt-out of targeted advertising, and the right to appeal.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">8. Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, comply with our legal obligations, resolve disputes, and enforce our agreements. When information is no longer needed, we securely delete or anonymize it.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">9. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These include encryption in transit and at rest, access controls, and regular security assessments. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence, including Canada and the United States. We ensure appropriate safeguards are in place for any such transfers in accordance with applicable privacy laws.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">11. Third-Party Links</h2>
            <p>
              Our services may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing personal information.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date. Your continued use of our services after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-[20px] font-medium text-[#202124] mb-3">13. Contact Us</h2>
            <div className="bg-[#f8f9fa] rounded-xl border border-[#e8eaed] p-6 mt-3">
              <p className="font-medium text-[#202124] mb-3">Eamico Inc.</p>
              <p>Subsidiary of Aikaro Technologies Corp.</p>
              <p>Federal Canadian Corporation</p>
              <p>Corporation Number: 1781131-4</p>
              <p>Business Number: 792360372</p>
              <div className="mt-4 space-y-1">
                <p><strong>Privacy Inquiries:</strong> <a href="mailto:privacy@aikarotech.com" className="text-[#ff9a4d] hover:underline">privacy@aikarotech.com</a></p>
                <p><strong>Legal Inquiries:</strong> <a href="mailto:legal@aikarotech.com" className="text-[#ff9a4d] hover:underline">legal@aikarotech.com</a></p>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
